import React, { useCallback, useEffect, useState } from 'react';
import { MessageSquare, ChevronRight, ChevronDown, ShieldAlert } from 'lucide-react';
import { validate } from '../../../validation/engine';
import ResponseShell from './ResponseShell';
import FormControl from '../../common/FormControl';
import Skeleton from '../../common/Skeleton';
import NoDataFound from '../../common/NoDataFound';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { useAutosave } from './useAutosave';
import { type ProcedureResponseProps, computeResponseReadOnly } from './responseShellTypes';
import {
  transitionService,
  procedureInstanceService,
  procedureTemplateService,
  walkthroughResponseService,
  riskAssessmentResponseService,
  controlTestResponseService,
  controlImplResponseService,
  findingService,
  procedureEvidenceService,
  type Finding,
  type WalkthroughResponseRow,
  type RiskAssessmentResponseRow,
  type ControlTestResponseRow,
  type ControlImplementationRow,
  type SopWalkthroughStepControl,
  type SopWalkthroughStep,
  type ProcedureEvidence,
} from '../../../services/auditService';
import { useAuth } from '../../../contexts/AuthContext';
import ObservationModal, { type ObservationModalRowContext } from './ObservationModal';
import RaiseFindingButton, {
  ITEM_TYPE_PROCESS_STEP,
  ITEM_TYPE_RISK,
  ITEM_TYPE_CONTROL,
} from './RaiseFindingButton';
import EvidenceCell from './EvidenceCell';
import InlineQuestionMeta from './InlineQuestionMeta';
import RejectNoteCell from './RejectNoteCell';
import {
  SeamlessSurface,
  SeamlessCard,
  TypeChip,
  AssessmentPanel,
  DualScorePanel,
  AssessRow,
  NetScore,
  SectionLabel,
  StatusButtons,
  ImplList,
  SeamlessSkeleton,
  riskBand,
  scoreBand,
} from './seamless/SeamlessKit';

/**
 * Plan 05c-05 — sample-less Walkthrough response body (STAFF-11).
 *
 * TWO render paths, dispatched on `isDesign`
 * (procedure_type === 'WalkthroughDesign'):
 *
 *   1. Compliance / legacy Walkthrough (`ComplianceWalkthrough`) — the FLAT
 *      checklist table: one row per SOP process_step (Complied/Not Complied/NA +
 *      observation + inline findings), with the read-only mitigating-controls
 *      cascade expandable per step. UNCHANGED behaviour.
 *
 *   2. Test of Design (`DesignWalkthrough`, plan 111-02) — a 3-layer nested
 *      SeamlessKit tree modelled on RiskAssessmentResponse.tsx:
 *        L1  Sub-process (SeamlessCard, status Pass/Fail/NA + observation →
 *              adt_walkthrough_responses, UNCHANGED service)
 *        L2  Risk (DERIVED client-side from the step's controls[].risks[]):
 *              DualScorePanel Inherent (Lik×Sev) + risk-level control (Ex×Ef) →
 *              Residual = round(Inherent × (1 − (Ex×Ef)/25)) →
 *              adt_risk_assessment_responses (carrying process_step_id)
 *        L3  Control: AssessmentPanel Ex×Ef (NetScore /25) →
 *              adt_control_test_responses (carrying risk_id + process_step_id)
 *        leaf Implementations: ImplList Pass/Fail/NA + observations →
 *              adt_control_implementation_responses (carrying risk_id + process_step_id)
 *      Findings raise at 3 levels: ITEM_TYPE_PROCESS_STEP (4) / ITEM_TYPE_RISK (9)
 *      / ITEM_TYPE_CONTROL (11). EVERY ToD risk/control/impl write carries a
 *      NON-NULL process_step_id (NULL≠NULL uniqueness landmine, plan 111-01).
 *
 * Finding-link mapping (CONTEXT D5):
 *   item_type_id  = ITEM_TYPE_PROCESS_STEP (4)
 *   item_record_id = process_step_id        (NOT response_id)
 * Rationale: a finding should pin to the SOP step the auditor walked, so it
 * survives even if the walkthrough_response row is later deleted/regenerated.
 *
 * Empty-SOP behaviour: if the template has no linked SOP / no steps, surface
 * an amber banner so the auditor knows to ask their DRC admin to attach an SOP.
 */

/** Test of Compliance scores adherence (Complied/NotComplied); Test of Design
 *  scores control-design adequacy (Pass/Fail). Same column, CHECK widened in
 *  migration 20260620_122148. */
type StatusVal = 'Complied' | 'NotComplied' | 'NA' | 'Pass' | 'Fail';

interface WalkthroughRow {
  /** Stable React key — "step-{process_step_id}". */
  key: string;
  /** GOV_process_steps.id; FK target of walkthrough_responses.process_step_id + finding item_record_id. */
  process_step_id: number;
  /** Display only. */
  step_number?: number | null;
  /** Display only. */
  step_name: string;
  /** Display only. */
  description?: string | null;
  /** Editable. '' = user has not yet picked a radio; treated as not-yet-savable. */
  status: StatusVal | '';
  /** Editable. */
  observation: string;
  /** Server-assigned after first save. */
  response_id?: number;
  /** G2 — checker per-line reject note (non-empty => line rejected). */
  reject_note: string;
  /** Test of Design — per-step mitigating controls cascade (read-only). */
  controls?: SopWalkthroughStepControl[];
  dirty: boolean;
}

/* ========================================================================== */
/* Compliance / legacy Walkthrough — FLAT checklist table (UNCHANGED).        */
/* ========================================================================== */

const ComplianceWalkthrough: React.FC<ProcedureResponseProps> = ({
  instance,
  role,
  performerName,
  approverName,
  onSaved,
  readOnly: readOnlyProp,
}) => {
  const { state } = useAuth();
  const entityId = state.user?.entity_id ?? 0;
  const currentUserId = state.user?.id ?? 0;
  // Read-only unless the maker (performer) is editing an open instance — shared rule.
  const readOnly = computeResponseReadOnly(instance, role, readOnlyProp);

  // Test of Design (WalkthroughDesign) scores control-design adequacy (Pass/Fail)
  // and shows each step's mitigating-control cascade; Test of Compliance / legacy
  // Walkthrough scores adherence (Complied/NotComplied) with no cascade.
  // procedure_type is present at runtime (the dispatcher routes on it) but not on
  // the ProcedureInstance prop type — cast.
  const isDesign = (instance as any).procedure_type === 'WalkthroughDesign';
  const statusOptions: Array<[StatusVal, string]> = isDesign
    ? [['Pass', 'Pass'], ['Fail', 'Fail'], ['NA', 'NA']]
    : [['Complied', 'Complied'], ['NotComplied', 'Not Complied'], ['NA', 'NA']];

  const [rows, setRows] = useState<WalkthroughRow[]>([]);
  const [sopMissing, setSopMissing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [obsErrors, setObsErrors] = useState<Record<string, string>>({});

  // Plan 05b-05 / STAFF-06 — inline finding raise state. Finding link target
  // is process_step_id (CONTEXT D5), so modalRow carries processStepId, not
  // response_id.
  const [findings, setFindings] = useState<Finding[]>([]);
  const [modalRow, setModalRow] = useState<{
    context: ObservationModalRowContext;
    processStepId: number;
  } | null>(null);
  // Observation captured in a small modal opened by the row's note icon
  // (checklist-parity Actions cluster). Holds the row index.
  const [remarksRow, setRemarksRow] = useState<number | null>(null);
  // Test of Design — which steps' mitigating-control cascade is expanded (by process_step_id).
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());

  // Phase 113 REQ-8 — per-step evidence, fetched once and grouped by process_step_id.
  const [evidenceByStep, setEvidenceByStep] = useState<Map<number, ProcedureEvidence[]>>(new Map());

  const reloadFindings = useCallback(async () => {
    try {
      const list = await findingService.listForProcedure(instance.id);
      setFindings(list);
    } catch {
      // Non-fatal — badges just won't show. Body still works.
    }
  }, [instance.id]);

  const reloadEvidence = useCallback(async () => {
    try {
      const list = await procedureEvidenceService.list(instance.id);
      const map = new Map<number, ProcedureEvidence[]>();
      for (const ev of list) {
        if (ev.item_type_id === ITEM_TYPE_PROCESS_STEP && ev.item_record_id != null) {
          const bucket = map.get(ev.item_record_id) ?? [];
          bucket.push(ev);
          map.set(ev.item_record_id, bucket);
        }
      }
      setEvidenceByStep(map);
    } catch {
      // Non-fatal.
    }
  }, [instance.id]);

  useEffect(() => {
    reloadFindings();
    reloadEvidence();
  }, [reloadFindings, reloadEvidence]);

  const findingsForStep = useCallback(
    (processStepId: number): Finding[] =>
      findings.filter(
        (f) =>
          f.item_type_id === ITEM_TYPE_PROCESS_STEP &&
          f.item_record_id === processStepId,
      ),
    [findings],
  );

  // Initial load — fetch procedure_template_id from the instance, then the
  // SOP context (steps), then any existing walkthrough_responses, then merge.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const inst = await procedureInstanceService.get(instance.id);
        const templateId = inst.procedure_template_id ?? null;

        let steps: Array<{
          id: number;
          step_number?: number | null;
          step_name: string;
          description?: string | null;
          controls?: SopWalkthroughStepControl[];
        }> = [];
        if (templateId) {
          try {
            const ctx = await procedureTemplateService.sopContext(templateId);
            steps = (ctx?.steps ?? []).map((s) => ({
              id: s.id,
              step_number: s.step_number ?? null,
              step_name: s.step_name ?? `Step ${s.id}`,
              description: s.description ?? null,
              controls: s.controls ?? [],
            }));
          } catch {
            // SOP context fetch failed — fall through to empty steps.
          }
        }

        if (steps.length === 0) {
          if (!cancelled) {
            setSopMissing(true);
            setRows([]);
          }
          return;
        }

        const responses: WalkthroughResponseRow[] =
          await walkthroughResponseService.list(instance.id);
        const byStepId = new Map<number, WalkthroughResponseRow>();
        for (const r of responses) byStepId.set(r.process_step_id, r);

        const merged: WalkthroughRow[] = steps.map((s) => {
          const existing = byStepId.get(s.id);
          return {
            key: `step-${s.id}`,
            process_step_id: s.id,
            step_number: s.step_number,
            step_name: s.step_name,
            description: s.description,
            status: (existing?.status as WalkthroughRow['status']) ?? '',
            observation: existing?.observation ?? '',
            response_id: existing?.id,
            reject_note: (existing as any)?.reject_note ?? '',
            controls: s.controls ?? [],
            dirty: false,
          };
        });

        if (!cancelled) {
          setRows(merged);
        }
      } catch (e: any) {
        if (!cancelled) {
          setLoadError(e?.message ?? 'Failed to load walkthrough.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instance.id]);

  // Debounced save — writes every dirty row that has a status picked.
  // Status is REQUIRED by the backend (NOT NULL); rows where the user has
  // typed an observation but not yet picked a radio are skipped until they do.
  const save = useCallback(async () => {
    const dirty = rows.filter((r) => r.dirty && r.status !== '' && !obsErrors[r.key]);
    if (dirty.length === 0) return;

    const updates: Array<{ key: string; response_id?: number }> = [];
    for (const r of dirty) {
      if (r.response_id) {
        await walkthroughResponseService.update(r.response_id, {
          status: r.status as StatusVal,
          observation: r.observation || null,
          modified_by: currentUserId,
        });
        updates.push({ key: r.key, response_id: r.response_id });
      } else {
        const created = await walkthroughResponseService.create({
          entity_id: entityId,
          procedure_instance_id: instance.id,
          process_step_id: r.process_step_id,
          status: r.status as StatusVal,
          observation: r.observation || null,
          created_by: currentUserId,
        });
        updates.push({ key: r.key, response_id: created?.id });
      }
    }

    setRows((prev) =>
      prev.map((r) => {
        const upd = updates.find((u) => u.key === r.key);
        if (!upd) return r;
        return { ...r, response_id: upd.response_id ?? r.response_id, dirty: false };
      }),
    );
  }, [rows, obsErrors, entityId, currentUserId, instance.id]);

  const { status, markDirty, flush } = useAutosave(save, 1000);

  const updateStatus = (idx: number, statusVal: StatusVal) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], status: statusVal, dirty: true };
      return next;
    });
    markDirty();
  };

  const updateObservation = (idx: number, observation: string, rowKey: string) => {
    setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], observation, dirty: true };
      return next;
    });
    markDirty();
  };

  const handleObsBlur = (rowKey: string, value: string) => {
    if (!value || !value.trim()) {
      setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
      return;
    }
    const result = validate(110, value);
    if (!result.valid) setObsErrors(prev => ({ ...prev, [rowKey]: result.message ?? 'Invalid observation.' }));
    else setObsErrors(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
  };

  // G2 — number of steps a checker has rejected (drives the shell approve-gate).
  const rejectedCount = rows.filter(r => r.reject_note && r.reject_note.trim()).length;

  // Submit gate (2026-06-29) — every step must carry a result before submit.
  // '' = the maker has not yet picked a radio.
  const unansweredSteps = rows.filter(r => r.status === '').length;
  const incompleteReason =
    rows.length > 0 && unansweredSteps > 0
      ? `Mark a result for every step before submitting — ${unansweredSteps} of ${rows.length} still blank.`
      : null;

  return (
    <ResponseShell
      instance={instance}
      procedureType="Walkthrough"
      role={role}
      performerName={performerName}
      approverName={approverName}
      autosaveStatus={status}
      rejectedCount={rejectedCount}
      incompleteReason={incompleteReason}
      onSaveDraft={async () => {
        await flush();
      }}
      onSubmit={async () => {
        await flush();
        await transitionService.submit('procedure', instance.id);
        onSaved?.();
      }}
      onApprove={async () => {
        await transitionService.approve('procedure', instance.id);
        onSaved?.();
      }}
      onReject={async (comment) => {
        await transitionService.reject('procedure', instance.id, comment);
        onSaved?.();
      }}
      onReopen={async () => {
        await transitionService.reopen('procedure', instance.id);
        onSaved?.();
      }}
    >
      {loadError && (
        <div className="inline-error inline-error--inline" role="alert">{loadError}</div>
      )}
      {sopMissing && (
        <NoDataFound
          title="No SOP attached"
          message="No SOP attached to this procedure (or the SOP has no steps). Ask your DRC admin to link an SOP via Procedure Templates → Walkthrough editor."
        />
      )}
      {/* Walkthrough = a compliance checklist over the SOP steps: one row per
          step (Complied/Not Complied/NA + observation + inline findings),
          rendered with the shared checklist table chrome. */}
      {rows.length === 0 && !loadError && !sopMissing && (
        <div className="response-section">
          <div className="table-list response-qtable">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <colgroup>
                  <col className="response-col-sno" />
                  <col />
                  <col className="response-col-answer" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="table-list-th response-th-sno">S.No</th>
                    <th className="table-list-th">Step</th>
                    <th className="table-list-th">Complied / Not Complied</th>
                  </tr>
                </thead>
                <tbody aria-busy={true} aria-live="polite">
                  <Skeleton.TableRows cols={['2xs', 'lg', 'md']} />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="response-section">
          <div className="table-list response-qtable">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <colgroup>
                  <col className="response-col-sno" />
                  <col />
                  <col className="response-col-answer" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="table-list-th response-th-sno">S.No</th>
                    <th className="table-list-th">Step</th>
                    <th className="table-list-th">Complied / Not Complied</th>
                  </tr>
                </thead>
                <tbody aria-live="polite">
                  {rows.map((r, idx) => {
                    const rowFindings = findingsForStep(r.process_step_id);
                    const stepEvidence = evidenceByStep.get(r.process_step_id) ?? [];
                    const radioName = `wt-${r.process_step_id}`;
                    const cascade = r.controls ?? [];
                    const hasCascade = isDesign && cascade.length > 0;
                    const expanded = expandedSteps.has(r.process_step_id);
                    const COL_COUNT = 3;
                    return (
                      <React.Fragment key={r.key}>
                        <tr className="table-list-row">
                          <td className="table-list-cell response-cell-sno">{idx + 1}</td>
                          <td className="table-list-cell response-cell-question">
                            {/* REQ-7: step name + description + action cluster all on one heading line. */}
                            <div className="response-question-row">
                              <span className="table-list-cell-title">{r.step_name}</span>
                              {r.description && <span className="table-list-cell-sub">: {r.description}</span>}
                              <div className="response-action-cluster response-action-cluster--inline">
                                <button
                                  type="button"
                                  className={`response-row-action${r.observation ? ' response-row-action--filled' : ''}`}
                                  aria-label="Add remark"
                                  title={r.observation ? 'Edit observation' : 'Add observation'}
                                  data-testid={`BTN_walkthrough_observation_${r.process_step_id}`}
                                  onClick={() => setRemarksRow(idx)}
                                  disabled={readOnly}
                                >
                                  <MessageSquare size={12} />
                                </button>
                                <EvidenceCell
                                  scope="row"
                                  entityId={entityId}
                                  createdBy={currentUserId}
                                  disabled={readOnly}
                                  procedureInstanceId={instance.id}
                                  itemTypeId={ITEM_TYPE_PROCESS_STEP}
                                  itemRecordId={r.process_step_id}
                                  questionLabel={r.step_name}
                                  showCount={false}
                                  onChanged={reloadEvidence}
                                />
                                <RejectNoteCell
                                  responseId={r.response_id}
                                  rejectNote={r.reject_note}
                                  procedureType="Walkthrough"
                                  role={role}
                                  readOnly={readOnly}
                                  onSaved={(note) =>
                                    setRows((prev) => prev.map((x) => (x.key === r.key ? { ...x, reject_note: note } : x)))
                                  }
                                />
                                <RaiseFindingButton
                                  count={rowFindings.length}
                                  showCount={false}
                                  disabled={readOnly}
                                  aria-label="Raise finding"
                                  title="Raise a finding from this step"
                                  onClick={() => {
                                    setModalRow({
                                      context: {
                                        procedureName: instance.audit_procedure_name,
                                        sampleLabel: r.step_name,
                                        responseValue: r.observation || null,
                                      },
                                      processStepId: r.process_step_id,
                                    });
                                  }}
                                />
                              </div>
                            </div>
                            {/* REQ-8 rev2: stacked Remarks / Attachments / Findings areas. */}
                            <InlineQuestionMeta
                              remark={r.observation}
                              evidence={stepEvidence}
                              findings={rowFindings}
                              readOnly={readOnly}
                              onEvidenceRemoved={reloadEvidence}
                              onRemarkClick={readOnly ? undefined : () => setRemarksRow(idx)}
                            />
                            {hasCascade && (
                              <button
                                type="button"
                                className="seamless-finding__raise"
                                style={{ marginTop: 4, fontSize: 12 }}
                                onClick={() => setExpandedSteps(prev => {
                                  const n = new Set(prev);
                                  if (n.has(r.process_step_id)) n.delete(r.process_step_id); else n.add(r.process_step_id);
                                  return n;
                                })}
                              >
                                {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                {cascade.length} mitigating control{cascade.length === 1 ? '' : 's'}
                              </button>
                            )}
                          </td>
                          {/* Single Yes/No column: Yes → Complied, No → NotComplied.
                              Pre-selects from saved status. DesignWalkthrough shows
                              Pass/Fail/NA via the statusOptions radios instead. */}
                          <td className="table-list-cell response-cell-answer--right">
                            {isDesign ? (
                              <div className="response-radio-group">
                                {statusOptions.map(([val, label]) => (
                                  <FormControl.Radio
                                    key={val}
                                    name={radioName}
                                    value={val}
                                    label={label}
                                    checked={r.status === val}
                                    onChange={() => updateStatus(idx, val)}
                                    disabled={readOnly}
                                    data-testid={`BTN_walkthrough_status_${r.process_step_id}_${val.toLowerCase()}`}
                                  />
                                ))}
                              </div>
                            ) : (
                              <div className="response-radio-group">
                                <FormControl.Radio
                                  name={radioName}
                                  value="Yes"
                                  label="Yes"
                                  checked={r.status === 'Complied'}
                                  onChange={() => updateStatus(idx, 'Complied')}
                                  disabled={readOnly}
                                  data-testid={`BTN_walkthrough_status_${r.process_step_id}_yes`}
                                />
                                <FormControl.Radio
                                  name={radioName}
                                  value="No"
                                  label="No"
                                  checked={r.status === 'NotComplied'}
                                  onChange={() => updateStatus(idx, 'NotComplied')}
                                  disabled={readOnly}
                                  data-testid={`BTN_walkthrough_status_${r.process_step_id}_no`}
                                />
                              </div>
                            )}
                          </td>
                        </tr>
                        {hasCascade && expanded && (
                          <tr className="table-list-row">
                            <td className="table-list-cell" />
                            <td colSpan={COL_COUNT - 1} className="table-list-cell" style={{ background: 'var(--gray-50, #f8fafc)' }}>
                              <div className="seamless__section-label" style={{ marginBottom: 8 }}>Mitigating Controls (read-only)</div>
                              <div className="tod-cascade">
                                {cascade.map(c => (
                                  <div key={c.control_id} className="tod-ctrl">
                                    <div className="tod-ctrl__head">
                                      <span className="tod-ctrl__name">{c.control_name}</span>
                                      <span className="tod-ctrl__code">{c.control_code}</span>
                                      {c.control_type && <span className="tod-ctrl__chip"><TypeChip label={c.control_type} /></span>}
                                    </div>
                                    {c.risks.length > 0 && (
                                      <div className="tod-ctrl__row">
                                        <span className="tod-ctrl__label">Risks</span>
                                        <div className="tod-ctrl__risks">
                                          {c.risks.map(rr => (
                                            <span key={rr.risk_id} className="tod-risk" title={rr.risk_name}>
                                              <ShieldAlert size={12} /> {rr.risk_code} · {rr.risk_name}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {c.implementations.length > 0 && (
                                      <div className="tod-ctrl__row">
                                        <span className="tod-ctrl__label">Implements</span>
                                        <ul className="tod-impls">
                                          {c.implementations.map(i => (
                                            <li key={i.id}>{i.explanation || i.implementation_type || `#${i.id}`}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Observation modal — captures the per-step observation (checklist parity). */}
      {remarksRow !== null && rows[remarksRow] && (() => {
        const ridx = remarksRow;
        const row = rows[ridx];
        return (
          <Modal isOpen onClose={() => setRemarksRow(null)} size="md">
            <Modal.Header onClose={() => setRemarksRow(null)}>Observation</Modal.Header>
            <Modal.Body>
              <p className="response-remarks-question">{row.step_name}</p>
              <FormControl.Textarea
                label="Observation"
                data-testid={`INP_walkthrough_observation_${row.process_step_id}`}
                value={row.observation}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateObservation(ridx, e.target.value, row.key)}
                onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => handleObsBlur(row.key, e.target.value)}
                rows={5}
                autoFocus
                disabled={readOnly}
                placeholder="What did you observe walking this step? (optional)"
              />
              {obsErrors[row.key] && <div className="response-inline-warning">{obsErrors[row.key]}</div>}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="fill" onClick={() => setRemarksRow(null)}>Done</Button>
            </Modal.Footer>
          </Modal>
        );
      })()}

      {/* Inline finding raise — polymorphic link is process_step_id (D5),
          NOT response_id. */}
      <ObservationModal
        data-testid="MOD_walkthrough_observation_modal"
        isOpen={modalRow !== null}
        onClose={() => setModalRow(null)}
        procedureInstanceId={instance.id}
        entityId={entityId}
        currentUserId={currentUserId}
        rowContext={modalRow?.context ?? { procedureName: instance.audit_procedure_name }}
        itemTypeId={ITEM_TYPE_PROCESS_STEP}
        itemRecordId={modalRow?.processStepId ?? null}
        onCreated={(f) => {
          setFindings((prev) => [...prev, f]);
          reloadFindings();
        }}
      />
    </ResponseShell>
  );
};

/* ========================================================================== */
/* Test of Design — 3-layer nested SeamlessKit tree (plan 111-02).            */
/* ========================================================================== */

/** L1 — one row per SOP sub-process (GOV_process_steps). Status Pass/Fail/NA +
 *  observation persist to adt_walkthrough_responses (UNCHANGED step-scoped). */
interface StepRow {
  key: string;                 // "step-{process_step_id}"
  process_step_id: number;
  step_number?: number | null;
  step_name: string;
  description?: string | null;
  status: StatusVal | '';
  observation: string;
  response_id?: number;
  reject_note: string;
  dirty: boolean;
}

/** L2 — a risk DERIVED from the sub-process's controls. Inherent (Lik×Sev) +
 *  risk-level control Ex×Ef → Residual. Persists to adt_risk_assessment_responses
 *  carrying process_step_id. */
interface DesignRiskRow {
  key: string;                 // "risk-{process_step_id}-{risk_id}"
  process_step_id: number;
  risk_id: number;
  risk_code?: string | null;
  risk_name?: string | null;
  likelihood: number | '';
  impact: number | '';
  existence_score: number | '';
  effectiveness_score: number | '';
  rationale: string;
  response_id?: number;
  dirty: boolean;
}

/** L3 — a control mitigating a risk under a sub-process. Ex×Ef (NetScore /25).
 *  Persists to adt_control_test_responses carrying risk_id + process_step_id. */
interface DesignControlRow {
  key: string;                 // "rc-{process_step_id}-{risk_id}-{control_id}"
  process_step_id: number;
  risk_id: number;
  control_id: number;
  control_code?: string | null;
  control_name?: string | null;
  control_type?: string | null;
  existence_score: number | '';
  effectiveness_score: number | '';
  result: 'Pass' | 'Fail' | 'NA' | '';
  evidence_text: string;
  response_id?: number;        // adt_control_test_responses.id
  dirty: boolean;
}

/** leaf — an implementation step under a control. Pass/Fail/NA + observations.
 *  Persists to adt_control_implementation_responses carrying risk_id + process_step_id. */
interface DesignImplRow {
  key: string;                 // "ri-{process_step_id}-{risk_id}-{implementation_id}"
  process_step_id: number;
  risk_id: number;
  control_id: number;
  implementation_id: number;
  sequence_number?: number | null;
  label: string;
  implementation_type?: string | null;
  response_id?: number | null;
  result: 'Pass' | 'Fail' | 'NA' | '';
  observations: string;
  dirty: boolean;
}

/** RemarksModal — per-row note editor as a popup (parity with RAT). */
const RemarksModal: React.FC<{
  heading: string;
  title: string;
  initialValue: string;
  readOnly: boolean;
  placeholder?: string;
  testId?: string;
  onCommit: (value: string) => void;
  onClose: () => void;
}> = ({ heading, title, initialValue, readOnly, placeholder, testId, onCommit, onClose }) => {
  const [draft, setDraft] = useState(initialValue);
  return (
    <Modal isOpen onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>{`Remarks under "${title}"`}</Modal.Header>
      <Modal.Body>
        <FormControl.Textarea
          label=""
          data-testid={testId}
          value={draft}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
          rows={5}
          placeholder={placeholder}
          autoFocus
          disabled={readOnly}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="fill" onClick={() => { onCommit(draft); onClose(); }} disabled={readOnly}>Done</Button>
      </Modal.Footer>
    </Modal>
  );
};

const DesignWalkthrough: React.FC<ProcedureResponseProps> = ({
  instance,
  role,
  performerName,
  approverName,
  onSaved,
  readOnly: readOnlyProp,
}) => {
  const { state } = useAuth();
  const entityId = state.user?.entity_id ?? 0;
  const currentUserId = state.user?.id ?? 0;
  // Read-only unless the maker (performer) is editing an open instance — shared rule.
  const readOnly = computeResponseReadOnly(instance, role, readOnlyProp);

  const statusOptions = [
    { val: 'Pass', label: 'Pass', mod: 'pass' as const },
    { val: 'Fail', label: 'Fail', mod: 'fail' as const },
    { val: 'NA', label: 'NA', mod: 'na' as const },
  ];

  const [steps, setSteps] = useState<StepRow[]>([]);
  const [risks, setRisks] = useState<DesignRiskRow[]>([]);
  const [controls, setControls] = useState<DesignControlRow[]>([]);
  const [impls, setImpls] = useState<DesignImplRow[]>([]);
  const [sopMissing, setSopMissing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [obsErrors, setObsErrors] = useState<Record<string, string>>({});

  const [findings, setFindings] = useState<Finding[]>([]);
  // Polymorphic finding modal — sub-process (process_step_id), risk (risk_id) or
  // control (control_id), each with its own ITEM_TYPE.
  const [modalRow, setModalRow] = useState<{
    context: ObservationModalRowContext;
    itemTypeId: number;
    itemRecordId: number;
  } | null>(null);
  // Phase 113 — per-step, per-risk, per-control evidence maps for InlineQuestionMeta.
  const [evidenceByStep, setEvidenceByStep] = useState<Map<number, ProcedureEvidence[]>>(new Map());
  const [evidenceByRisk, setEvidenceByRisk] = useState<Map<number, ProcedureEvidence[]>>(new Map());
  const [evidenceByControl, setEvidenceByControl] = useState<Map<number, ProcedureEvidence[]>>(new Map());

  const [remarks, setRemarks] = useState<
    { heading: string; title: string; value: string; placeholder?: string; testId?: string; onCommit: (v: string) => void } | null
  >(null);

  const reloadFindings = useCallback(async () => {
    try {
      const list = await findingService.listForProcedure(instance.id);
      setFindings(list);
    } catch {
      // Non-fatal — badges just won't show. Body still works.
    }
  }, [instance.id]);

  const reloadEvidence = useCallback(async () => {
    try {
      const list = await procedureEvidenceService.list(instance.id);
      const stepMap = new Map<number, ProcedureEvidence[]>();
      const riskMap = new Map<number, ProcedureEvidence[]>();
      const ctrlMap = new Map<number, ProcedureEvidence[]>();
      for (const ev of list) {
        if (ev.item_type_id === ITEM_TYPE_PROCESS_STEP && ev.item_record_id != null) {
          const bucket = stepMap.get(ev.item_record_id) ?? [];
          bucket.push(ev);
          stepMap.set(ev.item_record_id, bucket);
        } else if (ev.item_type_id === ITEM_TYPE_RISK && ev.item_record_id != null) {
          const bucket = riskMap.get(ev.item_record_id) ?? [];
          bucket.push(ev);
          riskMap.set(ev.item_record_id, bucket);
        } else if (ev.item_type_id === ITEM_TYPE_CONTROL && ev.item_record_id != null) {
          const bucket = ctrlMap.get(ev.item_record_id) ?? [];
          bucket.push(ev);
          ctrlMap.set(ev.item_record_id, bucket);
        }
      }
      setEvidenceByStep(stepMap);
      setEvidenceByRisk(riskMap);
      setEvidenceByControl(ctrlMap);
    } catch {
      // Non-fatal.
    }
  }, [instance.id]);

  useEffect(() => {
    reloadFindings();
    reloadEvidence();
  }, [reloadFindings, reloadEvidence]);

  const findingsForStep = useCallback(
    (processStepId: number): Finding[] =>
      findings.filter(
        (f) => f.item_type_id === ITEM_TYPE_PROCESS_STEP && f.item_record_id === processStepId,
      ),
    [findings],
  );
  const findingsForRisk = useCallback(
    (riskId: number): Finding[] =>
      findings.filter((f) => f.item_type_id === ITEM_TYPE_RISK && f.item_record_id === riskId),
    [findings],
  );
  const findingsForControl = useCallback(
    (controlId: number): Finding[] =>
      findings.filter((f) => f.item_type_id === ITEM_TYPE_CONTROL && f.item_record_id === controlId),
    [findings],
  );

  // Initial load — fetch the SOP cascade (steps[].controls[].risks[]/.implementations[]),
  // then build the 4-layer in-memory state, pre-filling saved scores from the 3
  // RAT response services. The cascade is control→risks; we INVERT to risk→controls
  // CLIENT-SIDE per step. Pre-fill strategy: list-ALL-per-instance once for each of
  // the 3 services, then bucket locally by the full tuple (lower-N than per-step
  // fetches — one GET per service instead of one per step).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const inst = await procedureInstanceService.get(instance.id);
        const templateId = inst.procedure_template_id ?? null;

        let sopSteps: SopWalkthroughStep[] = [];
        if (templateId) {
          try {
            const ctx = await procedureTemplateService.sopContext(templateId);
            sopSteps = ctx?.steps ?? [];
          } catch {
            // SOP context fetch failed — fall through to empty steps.
          }
        }

        if (sopSteps.length === 0) {
          if (!cancelled) {
            setSopMissing(true);
            setSteps([]);
            setRisks([]);
            setControls([]);
            setImpls([]);
          }
          return;
        }

        // L1 — saved walkthrough rows per step.
        const wtResponses: WalkthroughResponseRow[] =
          await walkthroughResponseService.list(instance.id);
        const wtByStep = new Map<number, WalkthroughResponseRow>();
        for (const r of wtResponses) wtByStep.set(r.process_step_id, r);

        // Saved RAT rows — list ALL per instance once, then bucket locally by the
        // full (process_step_id, risk_id[, control_id|implementation_id]) tuple.
        let savedRisks: RiskAssessmentResponseRow[] = [];
        let savedCtrl: ControlTestResponseRow[] = [];
        let savedImpl: ControlImplementationRow[] = [];
        try { savedRisks = await riskAssessmentResponseService.list(instance.id); } catch { /* non-fatal */ }
        try { savedCtrl = await controlTestResponseService.list(instance.id); } catch { /* non-fatal */ }
        try { savedImpl = await controlImplResponseService.list(instance.id); } catch { /* non-fatal */ }

        // Bucket maps keyed on the FULL tuple — only rows carrying a non-null
        // process_step_id participate in the ToD tree (legacy RAT rows ignored).
        const riskByKey = new Map<string, RiskAssessmentResponseRow>();
        for (const r of savedRisks) {
          if (r.process_step_id != null) riskByKey.set(`${r.process_step_id}-${r.risk_id}`, r);
        }
        const ctrlByKey = new Map<string, ControlTestResponseRow>();
        for (const c of savedCtrl) {
          if (c.process_step_id != null && c.risk_id != null) {
            ctrlByKey.set(`${c.process_step_id}-${c.risk_id}-${c.control_id}`, c);
          }
        }
        const implByKey = new Map<string, ControlImplementationRow>();
        for (const i of savedImpl) {
          if (i.process_step_id != null && i.risk_id != null) {
            implByKey.set(`${i.process_step_id}-${i.risk_id}-${i.implementation_id}`, i);
          }
        }

        const stepRows: StepRow[] = [];
        const riskRows: DesignRiskRow[] = [];
        const controlRows: DesignControlRow[] = [];
        const implRows: DesignImplRow[] = [];

        for (const s of sopSteps) {
          const stepId = s.id;
          const wt = wtByStep.get(stepId);
          stepRows.push({
            key: `step-${stepId}`,
            process_step_id: stepId,
            step_number: s.step_number ?? null,
            step_name: s.step_name ?? `Step ${stepId}`,
            description: s.description ?? null,
            status: (wt?.status as StatusVal) ?? '',
            observation: wt?.observation ?? '',
            response_id: wt?.id,
            reject_note: (wt as any)?.reject_note ?? '',
            dirty: false,
          });

          // CLIENT-SIDE regroup: invert control→risks into risk→controls for this
          // step. A control appears under EVERY risk it mitigates.
          const stepControls = s.controls ?? [];
          const riskMeta = new Map<number, { risk_code?: string | null; risk_name?: string | null }>();
          const controlsByRisk = new Map<number, SopWalkthroughStepControl[]>();
          for (const c of stepControls) {
            for (const rk of c.risks ?? []) {
              if (!riskMeta.has(rk.risk_id)) {
                riskMeta.set(rk.risk_id, { risk_code: rk.risk_code, risk_name: rk.risk_name });
              }
              const arr = controlsByRisk.get(rk.risk_id) ?? [];
              arr.push(c);
              controlsByRisk.set(rk.risk_id, arr);
            }
          }

          for (const [riskId, meta] of Array.from(riskMeta.entries())) {
            const savedR = riskByKey.get(`${stepId}-${riskId}`);
            riskRows.push({
              key: `risk-${stepId}-${riskId}`,
              process_step_id: stepId,
              risk_id: riskId,
              risk_code: meta.risk_code ?? null,
              risk_name: meta.risk_name ?? null,
              likelihood: savedR?.likelihood == null ? '' : savedR.likelihood,
              impact: savedR?.impact == null ? '' : savedR.impact,
              existence_score: savedR?.existence_score == null ? '' : savedR.existence_score,
              effectiveness_score: savedR?.effectiveness_score == null ? '' : savedR.effectiveness_score,
              rationale: savedR?.rationale ?? '',
              response_id: savedR?.id,
              dirty: false,
            });

            for (const c of controlsByRisk.get(riskId) ?? []) {
              const savedC = ctrlByKey.get(`${stepId}-${riskId}-${c.control_id}`);
              controlRows.push({
                key: `rc-${stepId}-${riskId}-${c.control_id}`,
                process_step_id: stepId,
                risk_id: riskId,
                control_id: c.control_id,
                control_code: c.control_code ?? null,
                control_name: c.control_name ?? null,
                control_type: c.control_type ?? null,
                existence_score: (savedC?.existence_score ?? '') as number | '',
                effectiveness_score: (savedC?.effectiveness_score ?? '') as number | '',
                result: (savedC?.result as DesignControlRow['result']) ?? '',
                evidence_text: savedC?.evidence_text ?? '',
                response_id: savedC?.id,
                dirty: false,
              });
              for (const im of c.implementations ?? []) {
                const savedI = implByKey.get(`${stepId}-${riskId}-${im.id}`);
                implRows.push({
                  key: `ri-${stepId}-${riskId}-${im.id}`,
                  process_step_id: stepId,
                  risk_id: riskId,
                  control_id: c.control_id,
                  implementation_id: im.id,
                  sequence_number: im.sequence_number ?? null,
                  label: im.explanation || `Implementation ${im.id}`,
                  implementation_type: im.implementation_type ?? null,
                  response_id: savedI?.response_id ?? null,
                  result: (savedI?.result as DesignImplRow['result']) ?? '',
                  observations: savedI?.observations ?? '',
                  dirty: false,
                });
              }
            }
          }
        }

        if (!cancelled) {
          setSteps(stepRows);
          setRisks(riskRows);
          setControls(controlRows);
          setImpls(implRows);
        }
      } catch (e: any) {
        if (!cancelled) {
          setLoadError(e?.message ?? 'Failed to load Test of Design.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instance.id]);

  // Debounced save — flushes dirty L1 (walkthrough), L2 (risk), L3 (control) and
  // leaf (impl) rows. EVERY risk/control/impl create+update payload carries a
  // non-null process_step_id (NULL≠NULL uniqueness landmine, plan 111-01).
  const save = useCallback(async () => {
    // 1. L1 sub-process status + observation (walkthroughResponseService, UNCHANGED).
    const dirtySteps = steps.filter((s) => s.dirty && s.status !== '' && !obsErrors[s.key]);
    const stepUpdates: Array<{ key: string; response_id?: number }> = [];
    for (const s of dirtySteps) {
      if (s.response_id) {
        await walkthroughResponseService.update(s.response_id, {
          status: s.status as StatusVal,
          observation: s.observation || null,
          modified_by: currentUserId,
        });
        stepUpdates.push({ key: s.key, response_id: s.response_id });
      } else {
        const created = await walkthroughResponseService.create({
          entity_id: entityId,
          procedure_instance_id: instance.id,
          process_step_id: s.process_step_id,
          status: s.status as StatusVal,
          observation: s.observation || null,
          created_by: currentUserId,
        });
        stepUpdates.push({ key: s.key, response_id: created?.id });
      }
    }

    // 2. L2 risk rows — Inherent L×S + risk-level Ex/Ef, carrying process_step_id.
    const dirtyRisks = risks.filter((r) => r.dirty && !obsErrors[r.key]);
    const riskUpdates: Array<{ key: string; response_id?: number }> = [];
    for (const r of dirtyRisks) {
      const rEx = r.existence_score === '' ? null : (r.existence_score as number);
      const rEf = r.effectiveness_score === '' ? null : (r.effectiveness_score as number);
      const hasAny =
        typeof r.likelihood === 'number' || typeof r.impact === 'number' || rEx != null || rEf != null;
      if (r.response_id) {
        await riskAssessmentResponseService.update(r.response_id, {
          process_step_id: r.process_step_id,
          likelihood: typeof r.likelihood === 'number' ? r.likelihood : null,
          impact: typeof r.impact === 'number' ? r.impact : null,
          existence_score: rEx,
          effectiveness_score: rEf,
          rationale: r.rationale || null,
          modified_by: currentUserId,
          // computed_score deliberately omitted — server recomputes.
        });
        riskUpdates.push({ key: r.key, response_id: r.response_id });
      } else if (hasAny) {
        const created = await riskAssessmentResponseService.create({
          entity_id: entityId,
          procedure_instance_id: instance.id,
          risk_id: r.risk_id,
          process_step_id: r.process_step_id,
          likelihood: typeof r.likelihood === 'number' ? r.likelihood : null,
          impact: typeof r.impact === 'number' ? r.impact : null,
          existence_score: rEx,
          effectiveness_score: rEf,
          rationale: r.rationale || null,
          created_by: currentUserId,
        });
        riskUpdates.push({ key: r.key, response_id: created?.id });
      }
    }

    // 3. L3 control rows — Ex/Ef/result/evidence, carrying risk_id + process_step_id.
    const dirtyControls = controls.filter((c) => c.dirty && !obsErrors[c.key]);
    const ctrlUpdates: Array<{ key: string; response_id?: number }> = [];
    for (const c of dirtyControls) {
      const ex = c.existence_score === '' ? null : c.existence_score;
      const ef = c.effectiveness_score === '' ? null : c.effectiveness_score;
      const hasScore = ex != null || ef != null;
      if (c.response_id) {
        await controlTestResponseService.update(c.response_id, {
          risk_id: c.risk_id,
          process_step_id: c.process_step_id,
          result: c.result === '' ? null : (c.result as 'Pass' | 'Fail' | 'NA'),
          evidence_text: c.evidence_text || null,
          existence_score: ex,
          effectiveness_score: ef,
          modified_by: currentUserId,
        });
        ctrlUpdates.push({ key: c.key, response_id: c.response_id });
      } else if (c.result !== '' || hasScore) {
        const created = await controlTestResponseService.create({
          entity_id: entityId,
          procedure_instance_id: instance.id,
          control_id: c.control_id,
          risk_id: c.risk_id,
          process_step_id: c.process_step_id,
          result: c.result === '' ? null : (c.result as 'Pass' | 'Fail' | 'NA'),
          evidence_text: c.evidence_text || null,
          existence_score: ex,
          effectiveness_score: ef,
          created_by: currentUserId,
        });
        ctrlUpdates.push({ key: c.key, response_id: created?.id });
      }
    }

    // 4. leaf impl rows — Pass/Fail/NA + observations, carrying risk_id + process_step_id.
    const dirtyImpls = impls.filter((i) => i.dirty);
    const implUpdates: Array<{ key: string; response_id?: number | null }> = [];
    for (const i of dirtyImpls) {
      if (i.response_id) {
        await controlImplResponseService.update(i.response_id, {
          risk_id: i.risk_id,
          process_step_id: i.process_step_id,
          result: i.result === '' ? null : (i.result as 'Pass' | 'Fail' | 'NA'),
          observations: i.observations || null,
        });
        implUpdates.push({ key: i.key, response_id: i.response_id });
      } else if (i.result !== '' || i.observations.trim() !== '') {
        const created = await controlImplResponseService.create({
          procedure_instance_id: instance.id,
          control_id: i.control_id,
          implementation_id: i.implementation_id,
          risk_id: i.risk_id,
          process_step_id: i.process_step_id,
          result: i.result === '' ? null : (i.result as 'Pass' | 'Fail' | 'NA'),
          observations: i.observations || null,
        });
        implUpdates.push({ key: i.key, response_id: created?.id });
      }
    }

    if (stepUpdates.length) {
      setSteps((prev) =>
        prev.map((s) => {
          const u = stepUpdates.find((x) => x.key === s.key);
          return u ? { ...s, response_id: u.response_id ?? s.response_id, dirty: false } : s;
        }),
      );
    }
    if (riskUpdates.length) {
      setRisks((prev) =>
        prev.map((r) => {
          const u = riskUpdates.find((x) => x.key === r.key);
          return u ? { ...r, response_id: u.response_id ?? r.response_id, dirty: false } : r;
        }),
      );
    }
    if (ctrlUpdates.length) {
      setControls((prev) =>
        prev.map((c) => {
          const u = ctrlUpdates.find((x) => x.key === c.key);
          return u ? { ...c, response_id: u.response_id ?? c.response_id, dirty: false } : c;
        }),
      );
    }
    if (implUpdates.length) {
      setImpls((prev) =>
        prev.map((i) => {
          const u = implUpdates.find((x) => x.key === i.key);
          return u ? { ...i, response_id: (u.response_id ?? i.response_id) ?? null, dirty: false } : i;
        }),
      );
    }
  }, [steps, risks, controls, impls, obsErrors, entityId, currentUserId, instance.id]);

  const { status, markDirty, flush } = useAutosave(save, 1000);

  const updateStep = (key: string, patch: Partial<StepRow>) => {
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch, dirty: true } : s)));
    markDirty();
  };
  const updateRisk = (key: string, patch: Partial<DesignRiskRow>) => {
    setRisks((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch, dirty: true } : r)));
    markDirty();
  };
  const updateControl = (key: string, patch: Partial<DesignControlRow>) => {
    setControls((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch, dirty: true } : c)));
    markDirty();
  };
  const updateImpl = (key: string, patch: Partial<DesignImplRow>) => {
    setImpls((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch, dirty: true } : i)));
    markDirty();
  };

  const handleObsBlur = (rowKey: string, value: string) => {
    if (!value || !value.trim()) {
      setObsErrors((prev) => { const n = { ...prev }; delete n[rowKey]; return n; });
      return;
    }
    const result = validate(110, value);
    if (!result.valid) setObsErrors((prev) => ({ ...prev, [rowKey]: result.message ?? 'Invalid text.' }));
    else setObsErrors((prev) => { const n = { ...prev }; delete n[rowKey]; return n; });
  };

  // G2 — number of sub-processes a checker has rejected (drives the shell approve-gate).
  const rejectedCount = steps.filter((s) => s.reject_note && s.reject_note.trim()).length;

  // Submit gate (2026-06-29) — every sub-process (L1) must carry a Pass/Fail/NA
  // result before submit. The nested risk/control/impl scores stay optional (a
  // step may legitimately have no mitigating controls). '' = not yet picked.
  const unansweredSteps = steps.filter((s) => s.status === '').length;
  const incompleteReason =
    steps.length > 0 && unansweredSteps > 0
      ? `Mark a result for every sub-process before submitting — ${unansweredSteps} of ${steps.length} still blank.`
      : null;

  return (
    <ResponseShell
      instance={instance}
      procedureType="Walkthrough"
      role={role}
      performerName={performerName}
      approverName={approverName}
      autosaveStatus={status}
      rejectedCount={rejectedCount}
      incompleteReason={incompleteReason}
      onSaveDraft={async () => {
        await flush();
      }}
      onSubmit={async () => {
        await flush();
        await transitionService.submit('procedure', instance.id);
        onSaved?.();
      }}
      onApprove={async () => {
        await transitionService.approve('procedure', instance.id);
        onSaved?.();
      }}
      onReject={async (comment) => {
        await transitionService.reject('procedure', instance.id, comment);
        onSaved?.();
      }}
      onReopen={async () => {
        await transitionService.reopen('procedure', instance.id);
        onSaved?.();
      }}
    >
      {loadError && (
        <div className="inline-error inline-error--inline" role="alert">{loadError}</div>
      )}
      {sopMissing && (
        <NoDataFound
          title="No SOP attached"
          message="No SOP attached to this procedure (or the SOP has no steps). Ask your DRC admin to link an SOP via Procedure Templates → Walkthrough editor."
        />
      )}
      {steps.length > 0 && (
        <SeamlessSurface className="seamless-tight">
          {steps.map((s, sIdx) => {
            const stepFindings = findingsForStep(s.process_step_id);
            const stepRisks = risks.filter((r) => r.process_step_id === s.process_step_id);
            return (
              <SeamlessCard
                key={s.key}
                sno={sIdx + 1}
                snoLabel={`S${s.step_number ?? sIdx + 1}`}
                variant="subprocess"
                title={s.step_name}
                sub={s.description}
                assessment={
                  <StatusButtons
                    options={statusOptions}
                    value={s.status}
                    disabled={readOnly}
                    onChange={(val) => updateStep(s.key, { status: (val as StatusVal | '') })}
                    testId={(val) => `BTN_walkthrough_status_${s.process_step_id}_${val.toLowerCase()}`}
                  />
                }
                inlineActions={
                  <div className="response-action-cluster response-action-cluster--inline">
                    <button
                      type="button"
                      className="response-row-action"
                      aria-label="Add remark"
                      title={s.observation ? 'Edit observation' : 'Add observation'}
                      data-testid={`BTN_tod_step_note_${s.process_step_id}`}
                      onClick={() =>
                        setRemarks({
                          heading: 'Observation',
                          title: s.step_name,
                          value: s.observation,
                          placeholder: 'What did you observe walking this step? (optional)',
                          testId: `INP_walkthrough_observation_${s.process_step_id}`,
                          onCommit: (v) => {
                            updateStep(s.key, { observation: v });
                            handleObsBlur(s.key, v);
                          },
                        })
                      }
                      disabled={readOnly}
                    >
                      <MessageSquare size={12} />
                    </button>
                    <EvidenceCell
                      scope="row"
                      entityId={entityId}
                      createdBy={currentUserId}
                      disabled={readOnly}
                      procedureInstanceId={instance.id}
                      itemTypeId={ITEM_TYPE_PROCESS_STEP}
                      itemRecordId={s.process_step_id}
                      questionLabel={s.step_name}
                      showCount={false}
                      onChanged={reloadEvidence}
                    />
                    <RaiseFindingButton
                      count={stepFindings.length}
                      showCount={false}
                      disabled={readOnly}
                      title="Raise a finding from this sub-process"
                      data-testid={`BTN_tod_step_finding_${s.process_step_id}`}
                      onClick={() => {
                        setModalRow({
                          context: {
                            procedureName: instance.audit_procedure_name,
                            sampleLabel: s.step_name,
                            responseValue: s.observation || null,
                          },
                          itemTypeId: ITEM_TYPE_PROCESS_STEP,
                          itemRecordId: s.process_step_id,
                        });
                      }}
                    />
                  </div>
                }
                inlineRemark={
                  <InlineQuestionMeta
                    remark={s.observation}
                    evidence={evidenceByStep.get(s.process_step_id) ?? []}
                    findings={stepFindings}
                    readOnly={readOnly}
                    onEvidenceRemoved={reloadEvidence}
                    onRemarkClick={readOnly ? undefined : () =>
                      setRemarks({
                        heading: 'Observation',
                        title: s.step_name,
                        value: s.observation,
                        placeholder: 'What did you observe walking this step? (optional)',
                        testId: `INP_walkthrough_observation_${s.process_step_id}`,
                        onCommit: (v) => {
                          updateStep(s.key, { observation: v });
                          handleObsBlur(s.key, v);
                        },
                      })
                    }
                  />
                }
                impl={
                  <>
                    {stepRisks.length === 0 && (
                      <p className="seamless__impl-sub" style={{ padding: '8px 0' }}>No risks for this sub-process.</p>
                    )}
                    {stepRisks.map((r, rIdx) => {
                      const riskFindings = findingsForRisk(r.risk_id);
                      const lik = r.likelihood === '' ? 0 : (r.likelihood as number);
                      const sev = r.impact === '' ? 0 : (r.impact as number);
                      const inherent = lik * sev;
                      const rEx = r.existence_score === '' ? 0 : (r.existence_score as number);
                      const rEf = r.effectiveness_score === '' ? 0 : (r.effectiveness_score as number);
                      const effPct = (rEx * rEf) / 25;
                      const defPct = 1 - effPct;
                      const residual = Math.round(inherent * defPct);
                      const riskControls = controls.filter(
                        (c) => c.process_step_id === s.process_step_id && c.risk_id === r.risk_id,
                      );
                      return (
                        <SeamlessCard
                          key={r.key}
                          sno={rIdx + 1}
                          snoLabel={`R${rIdx + 1}`}
                          variant="risk"
                          title={r.risk_name ?? 'Unknown risk'}
                          assessment={
                            <DualScorePanel
                              row1={
                                <>
                                  <AssessRow
                                    label="Lik"
                                    labelTitle="Likelihood"
                                    tone="risk"
                                    value={r.likelihood}
                                    disabled={readOnly}
                                    onChange={(v) => updateRisk(r.key, { likelihood: v })}
                                    testId={`SEL_tod_risk_likelihood_${s.process_step_id}_${r.risk_id}`}
                                  />
                                  <AssessRow
                                    label="Sev"
                                    labelTitle="Severity"
                                    tone="risk"
                                    value={r.impact}
                                    disabled={readOnly}
                                    onChange={(v) => updateRisk(r.key, { impact: v })}
                                    testId={`SEL_tod_risk_severity_${s.process_step_id}_${r.risk_id}`}
                                  />
                                </>
                              }
                              verdict1={
                                <NetScore
                                  text={`${inherent}`}
                                  variant={riskBand(inherent)}
                                  title={`Inherent risk = Likelihood × Severity = ${inherent} (out of 25)`}
                                  testId={`LBL_tod_risk_inherent_${s.process_step_id}_${r.risk_id}`}
                                />
                              }
                              row2={
                                <>
                                  <AssessRow
                                    label="Ex"
                                    labelTitle="Existence (control)"
                                    tone="control"
                                    value={r.existence_score}
                                    disabled={readOnly}
                                    onChange={(v) => updateRisk(r.key, { existence_score: v })}
                                    testId={`SEL_tod_risk_existence_${s.process_step_id}_${r.risk_id}`}
                                  />
                                  <AssessRow
                                    label="Ef"
                                    labelTitle="Effectiveness (control)"
                                    tone="control"
                                    value={r.effectiveness_score}
                                    disabled={readOnly}
                                    onChange={(v) => updateRisk(r.key, { effectiveness_score: v })}
                                    testId={`SEL_tod_risk_effectiveness_${s.process_step_id}_${r.risk_id}`}
                                  />
                                </>
                              }
                              verdict2={
                                <NetScore
                                  text={`${residual}`}
                                  variant={riskBand(residual)}
                                  title={`Residual = Inherent ${inherent} × Deficiency ${Math.round(defPct * 100)}% = ${residual} (controls offset ${Math.round(effPct * 100)}%)`}
                                  testId={`LBL_tod_risk_residual_${s.process_step_id}_${r.risk_id}`}
                                  emphasis
                                />
                              }
                            />
                          }
                          inlineActions={
                            <div className="response-action-cluster response-action-cluster--inline">
                              <button
                                type="button"
                                className="response-row-action"
                                aria-label="Add remark"
                                title={r.rationale ? 'Edit rationale' : 'Add rationale'}
                                data-testid={`BTN_tod_risk_note_${s.process_step_id}_${r.risk_id}`}
                                onClick={() =>
                                  setRemarks({
                                    heading: 'Rationale',
                                    title: r.risk_name ?? 'Risk',
                                    value: r.rationale,
                                    placeholder: 'Why this likelihood and impact? (optional)',
                                    testId: `INP_tod_risk_rationale_${s.process_step_id}_${r.risk_id}`,
                                    onCommit: (v) => {
                                      updateRisk(r.key, { rationale: v });
                                      handleObsBlur(r.key, v);
                                    },
                                  })
                                }
                                disabled={readOnly}
                              >
                                <MessageSquare size={12} />
                              </button>
                              <EvidenceCell
                                scope="row"
                                entityId={entityId}
                                createdBy={currentUserId}
                                disabled={readOnly}
                                procedureInstanceId={instance.id}
                                itemTypeId={ITEM_TYPE_RISK}
                                itemRecordId={r.risk_id}
                                questionLabel={r.risk_name}
                                showCount={false}
                                onChanged={reloadEvidence}
                              />
                              <RaiseFindingButton
                                count={riskFindings.length}
                                showCount={false}
                                disabled={readOnly}
                                title="Raise a vulnerability"
                                data-testid={`BTN_tod_risk_finding_${s.process_step_id}_${r.risk_id}`}
                                onClick={() => {
                                  setModalRow({
                                    context: {
                                      procedureName: instance.audit_procedure_name,
                                      sampleLabel: r.risk_name ?? 'Unknown risk',
                                      responseValue: r.rationale || (inherent > 0 ? `Score ${inherent}` : null),
                                    },
                                    itemTypeId: ITEM_TYPE_RISK,
                                    itemRecordId: r.risk_id,
                                  });
                                }}
                              />
                            </div>
                          }
                          inlineRemark={
                            <InlineQuestionMeta
                              evidence={evidenceByRisk.get(r.risk_id) ?? []}
                              findings={riskFindings}
                              readOnly={readOnly}
                              onEvidenceRemoved={reloadEvidence}
                              onRemarkClick={readOnly ? undefined : () =>
                                setRemarks({
                                  heading: 'Rationale',
                                  title: r.risk_name ?? 'Risk',
                                  value: r.rationale,
                                  placeholder: 'Why this likelihood and impact? (optional)',
                                  testId: `INP_tod_risk_rationale_${s.process_step_id}_${r.risk_id}`,
                                  onCommit: (v) => {
                                    updateRisk(r.key, { rationale: v });
                                    handleObsBlur(r.key, v);
                                  },
                                })
                              }
                            />
                          }
                          impl={
                            <>
                              {riskControls.length === 0 && (
                                <p className="seamless__impl-sub" style={{ padding: '8px 0' }}>No mitigating controls.</p>
                              )}
                              {riskControls.map((c, cIdx) => {
                                const ctrlFindings = findingsForControl(c.control_id);
                                const net =
                                  (c.existence_score === '' ? 0 : (c.existence_score as number)) *
                                  (c.effectiveness_score === '' ? 0 : (c.effectiveness_score as number));
                                const cVariant = scoreBand(net);
                                const implsForControl = impls.filter(
                                  (i) =>
                                    i.process_step_id === s.process_step_id &&
                                    i.risk_id === r.risk_id &&
                                    i.control_id === c.control_id,
                                );
                                return (
                                  <SeamlessCard
                                    key={c.key}
                                    sno={cIdx + 1}
                                    snoLabel={`C${cIdx + 1}`}
                                    variant="control"
                                    title={c.control_name ?? 'Unknown control'}
                                    typeChip={<TypeChip label={c.control_type} />}
                                    assessment={
                                      <AssessmentPanel
                                        verdict={
                                          <NetScore
                                            text={`${net}/25`}
                                            variant={cVariant}
                                            title="Net Score = Existence × Effectiveness (out of 25)"
                                            testId={`LBL_tod_control_net_score_${s.process_step_id}_${r.risk_id}_${c.control_id}`}
                                          />
                                        }
                                      >
                                        <AssessRow
                                          label="Ex"
                                          labelTitle="Existence"
                                          tone="control"
                                          value={c.existence_score}
                                          disabled={readOnly}
                                          onChange={(v) => updateControl(c.key, { existence_score: v })}
                                          testId={`SEL_tod_control_existence_${s.process_step_id}_${r.risk_id}_${c.control_id}`}
                                        />
                                        <AssessRow
                                          label="Ef"
                                          labelTitle="Effectiveness"
                                          tone="control"
                                          value={c.effectiveness_score}
                                          disabled={readOnly}
                                          onChange={(v) => updateControl(c.key, { effectiveness_score: v })}
                                          testId={`SEL_tod_control_effectiveness_${s.process_step_id}_${r.risk_id}_${c.control_id}`}
                                        />
                                      </AssessmentPanel>
                                    }
                                    inlineRemark={
                                      <InlineQuestionMeta
                                        evidence={evidenceByControl.get(c.control_id) ?? []}
                                        findings={ctrlFindings}
                                        readOnly={readOnly}
                                        onEvidenceRemoved={reloadEvidence}
                                        onRemarkClick={readOnly ? undefined : () =>
                                          setRemarks({
                                            heading: 'Evidence note',
                                            title: c.control_name ?? 'Control',
                                            value: c.evidence_text,
                                            placeholder: 'Note the evidence you reviewed (optional)',
                                            testId: `INP_tod_control_evidence_text_${c.key}`,
                                            onCommit: (v) => {
                                              setObsErrors((prev) => { const n = { ...prev }; delete n[c.key]; return n; });
                                              updateControl(c.key, { evidence_text: v });
                                              handleObsBlur(c.key, v);
                                            },
                                          })
                                        }
                                      />
                                    }
                                    impl={
                                      implsForControl.length > 0 ? (
                                        <>
                                          <SectionLabel>Implementations</SectionLabel>
                                          <ImplList
                                            items={implsForControl.map((im) => ({
                                              id: im.implementation_id,
                                              sno: im.sequence_number,
                                              title: im.label,
                                              sub: im.implementation_type ? `(${im.implementation_type})` : null,
                                              result: im.result,
                                              observations: im.observations,
                                            }))}
                                            onResult={(id, result) =>
                                              updateImpl(`ri-${s.process_step_id}-${r.risk_id}-${id}`, { result: result as DesignImplRow['result'] })
                                            }
                                            onObservations={(id, text) =>
                                              updateImpl(`ri-${s.process_step_id}-${r.risk_id}-${id}`, { observations: text })
                                            }
                                            onOpenObservation={(id) => {
                                              const im = implsForControl.find((x) => x.implementation_id === id);
                                              setRemarks({
                                                heading: 'Observation',
                                                title: im?.label ?? 'Implementation',
                                                value: im?.observations ?? '',
                                                placeholder: 'Observation (optional)',
                                                testId: `INP_tod_control_impl_obs_${s.process_step_id}_${r.risk_id}_${id}`,
                                                onCommit: (v) => updateImpl(`ri-${s.process_step_id}-${r.risk_id}-${id}`, { observations: v }),
                                              });
                                            }}
                                            disabled={readOnly}
                                            testIdPrefix={`tod_control_impl_${s.process_step_id}_${r.risk_id}`}
                                          />
                                        </>
                                      ) : undefined
                                    }
                                    inlineActions={
                                      <div className="response-action-cluster response-action-cluster--inline">
                                        <button
                                          type="button"
                                          className="response-row-action"
                                          aria-label="Add remark"
                                          title={c.evidence_text ? 'Edit evidence note' : 'Add evidence note'}
                                          data-testid={`BTN_tod_control_note_${s.process_step_id}_${r.risk_id}_${c.control_id}`}
                                          onClick={() =>
                                            setRemarks({
                                              heading: 'Evidence note',
                                              title: c.control_name ?? 'Control',
                                              value: c.evidence_text,
                                              placeholder: 'Note the evidence you reviewed (optional)',
                                              testId: `INP_tod_control_evidence_text_${c.key}`,
                                              onCommit: (v) => {
                                                setObsErrors((prev) => { const n = { ...prev }; delete n[c.key]; return n; });
                                                updateControl(c.key, { evidence_text: v });
                                                handleObsBlur(c.key, v);
                                              },
                                            })
                                          }
                                          disabled={readOnly}
                                        >
                                          <MessageSquare size={12} />
                                        </button>
                                        <EvidenceCell
                                          scope="row"
                                          entityId={entityId}
                                          createdBy={currentUserId}
                                          disabled={readOnly}
                                          procedureInstanceId={instance.id}
                                          itemTypeId={ITEM_TYPE_CONTROL}
                                          itemRecordId={c.control_id}
                                          questionLabel={c.control_name}
                                          showCount={false}
                                          onChanged={reloadEvidence}
                                        />
                                        <RaiseFindingButton
                                          count={ctrlFindings.length}
                                          showCount={false}
                                          disabled={readOnly}
                                          title="Raise a deficiency"
                                          data-testid={`BTN_tod_control_finding_${s.process_step_id}_${r.risk_id}_${c.control_id}`}
                                          onClick={() => {
                                            setModalRow({
                                              context: {
                                                procedureName: instance.audit_procedure_name,
                                                sampleLabel: c.control_name ?? 'Unknown control',
                                                responseValue: c.evidence_text || (net > 0 ? `Score ${net}/25` : null),
                                              },
                                              itemTypeId: ITEM_TYPE_CONTROL,
                                              itemRecordId: c.control_id,
                                            });
                                          }}
                                        />
                                      </div>
                                    }
                                  />
                                );
                              })}
                            </>
                          }
                        />
                      );
                    })}
                  </>
                }
                footer={
                  <RejectNoteCell
                    responseId={s.response_id}
                    rejectNote={s.reject_note}
                    procedureType="Walkthrough"
                    role={role}
                    readOnly={readOnly}
                    onSaved={(note) =>
                      setSteps((prev) => prev.map((x) => (x.key === s.key ? { ...x, reject_note: note } : x)))
                    }
                  />
                }
              />
            );
          })}
        </SeamlessSurface>
      )}
      {steps.length === 0 && !loadError && !sopMissing && <SeamlessSkeleton />}

      {/* Polymorphic finding raise — sub-process (4) / risk (9) / control (11). */}
      <ObservationModal
        data-testid="MOD_walkthrough_observation_modal"
        isOpen={modalRow !== null}
        onClose={() => setModalRow(null)}
        procedureInstanceId={instance.id}
        entityId={entityId}
        currentUserId={currentUserId}
        rowContext={modalRow?.context ?? { procedureName: instance.audit_procedure_name }}
        itemTypeId={modalRow?.itemTypeId ?? ITEM_TYPE_PROCESS_STEP}
        itemRecordId={modalRow?.itemRecordId ?? null}
        onCreated={(f) => {
          setFindings((prev) => [...prev, f]);
          reloadFindings();
        }}
      />
      {remarks && (
        <RemarksModal
          heading={remarks.heading}
          title={remarks.title}
          initialValue={remarks.value}
          placeholder={remarks.placeholder}
          testId={remarks.testId}
          readOnly={readOnly}
          onCommit={remarks.onCommit}
          onClose={() => setRemarks(null)}
        />
      )}
    </ResponseShell>
  );
};

/* ========================================================================== */
/* Dispatcher — Test of Design → nested tree; everything else → flat table.    */
/* ========================================================================== */

const WalkthroughResponse: React.FC<ProcedureResponseProps> = (props) => {
  const isDesign = (props.instance as any).procedure_type === 'WalkthroughDesign';
  return isDesign ? <DesignWalkthrough {...props} /> : <ComplianceWalkthrough {...props} />;
};

export default WalkthroughResponse;
